import { Link } from "react-router-dom";
import { ArrowUpRight, Wheat, MessageCircle, Sprout, Heart, MapPin, Calendar } from "lucide-react";
import Marquee from "../components/Marquee";
import { BRAND } from "../data/content";

const PHILOSOPHY = [
  { icon: Wheat, t: "Real Food", d: "Millet-led. Made fresh. No maida, no preservatives, no shortcuts." },
  { icon: MessageCircle, t: "Real Conversations", d: "Spaces and seating designed so you can actually hear each other." },
  { icon: Sprout, t: "Real Impact", d: "Working with millet farmers. Reviving an ancient grain for a new generation." },
];

const VALUES = [
  { t: "Made with millet", d: "9 categories, all crafted around India's most underrated grain." },
  { t: "Wholesome food", d: "Real ingredients, slow cooking, fresh bakes every morning." },
  { t: "Spaces that gather people", d: "Couple corners, work nooks, community tables." },
  { t: "Thoughtful by design", d: "Warm lighting, comfortable seating, charging, Wi-Fi." },
  { t: "Community-led events", d: "Open mics, book clubs, founder meetups, workshops." },
  { t: "For every you", d: "Date night. Solo work. Sunday brunch. Book Saturday." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-28">
      {/* HERO */}
      <section className="py-20 sm:py-28 bg-grain">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 text-center">
          <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">Our Story</span>
          <h1 className="font-serif-display text-5xl sm:text-7xl lg:text-8xl leading-[1] mt-6">
            Good food. <br />
            Real <em className="italic text-[#C76D4B]">conversations.</em> <br />
            Everyday stories.
          </h1>
          <p className="mt-10 text-base sm:text-lg text-[#7A5C45] max-w-2xl mx-auto leading-relaxed">
            We started Stories because the world needed somewhere to slow down. A café where
            the menu honours an ancient grain, the chairs invite a long sit, and every visit
            ends in a story worth telling.
          </p>
        </div>
      </section>

      {/* HERO IMAGE */}
      <section className="px-5 sm:px-8">
        <div className="max-w-6xl mx-auto overflow-hidden rounded-[2rem] border border-[#E8D8C8]">
          <img
            src={BRAND.heroAmbience}
            alt="Stories Café"
            className="w-full h-[480px] object-cover"
          />
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl">
            <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">The Philosophy</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]">
              Three words. One <em className="italic">café.</em>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            {PHILOSOPHY.map((p, i) => (
              <div key={p.t} className="bg-[#F3EBE1] rounded-3xl p-8 card-lift" data-testid={`philosophy-${i}`}>
                <div className="h-12 w-12 rounded-full bg-white grid place-items-center border border-[#E8D8C8]">
                  <p.icon size={18} className="text-[#C76D4B]" />
                </div>
                <h3 className="font-serif-display text-3xl mt-6">{p.t}</h3>
                <p className="text-sm text-[#7A5C45] mt-3 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Marquee items={["Real Food", "Real Conversations", "Real Impact"]} />

      {/* COME FOR THE FOOD */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative">
            <div className="overflow-hidden rounded-[2rem]">
              <img
                src={BRAND.comeForTheFood}
                alt="The Stories millet spread"
                className="w-full h-[520px] object-cover img-zoom"
              />
            </div>
            <span className="absolute -bottom-4 left-6 font-script text-[#C76D4B] text-3xl rotate-[-3deg]">
              taste the grain
            </span>
          </div>
          <div className="lg:col-span-6">
            <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">Come for the food.</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 leading-[1.05]">
              Millet, made <em className="italic">aspirational.</em>
            </h2>
            <p className="text-[#7A5C45] mt-6 leading-relaxed">
              Foxtail, pearl, little, ragi, jowar, bajra, kodo — each grain finds a home on our
              menu. We work with farmers across India to bring you risottos, pastas, pizzas,
              brownies and beverages that don't just sound healthy. They taste like the
              best version of comfort food.
            </p>
            <Link to="/menu" className="btn-secondary mt-8" data-testid="about-view-menu">
              See the Menu <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* STAY FOR THE CONVERSATION */}
      <section className="py-24 sm:py-32 bg-[#F3EBE1]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">Stay for the conversation.</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 leading-[1.05]">
              Spaces designed for <em className="italic">dwell time.</em>
            </h2>
            <p className="text-[#7A5C45] mt-6 leading-relaxed">
              Warm lighting, soft fabrics, low-volume jazz on Sunday afternoons. Charging points
              for the founders. Quiet windows for the readers. A long bench for the families
              who turn dinner into a four-hour catch-up.
            </p>
            <div className="grid grid-cols-2 gap-4 mt-8">
              {["Warm lighting", "Comfort seating", "Quiet corners", "Community energy"].map((t, i) => (
                <div key={t} className="bg-white rounded-2xl p-4 border border-[#E8D8C8]" data-testid={`dwell-${i}`}>
                  <Heart size={14} className="text-[#C76D4B]" />
                  <div className="font-medium text-sm mt-3">{t}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2 overflow-hidden rounded-[2rem]">
            <img
              src={BRAND.spacesDwellTime}
              alt="Spaces designed for dwell time at Stories"
              className="w-full h-[520px] object-cover img-zoom"
            />
          </div>
        </div>
      </section>

      {/* VALUES GRID */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">What we stand for</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-5">For every <em className="italic">you.</em></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {VALUES.map((v, i) => (
              <div key={v.t} className="border border-[#E8D8C8] rounded-3xl p-7 card-lift bg-[#FAF7F2]" data-testid={`value-${i}`}>
                <div className="text-xs tracking-[0.22em] uppercase text-[#C76D4B]">0{i + 1}</div>
                <h3 className="font-serif-display text-2xl mt-3">{v.t}</h3>
                <p className="text-sm text-[#7A5C45] mt-3">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#2A1E17] text-[#FAF7F2] overflow-hidden">
        <img
          src={BRAND.milletGraphic}
          alt=""
          aria-hidden="true"
          className="absolute -left-16 -top-10 w-80 opacity-20 rotate-[-20deg] pointer-events-none select-none"
        />
        <img
          src={BRAND.milletGraphic}
          alt=""
          aria-hidden="true"
          className="absolute -right-16 bottom-0 w-72 opacity-15 rotate-[170deg] pointer-events-none select-none"
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight">
            Visit a café. Stay for a <em className="italic text-[#D4A373]">while.</em>
          </h2>
          <div className="flex flex-wrap gap-4">
            <Link to="/locations" className="btn-primary bg-[#C76D4B] hover:bg-[#A55639]" data-testid="about-find-cafe">
              <MapPin size={18} /> Find a Café
            </Link>
            <Link to="/events" className="btn-secondary border-[#FAF7F2] text-[#FAF7F2] hover:bg-[#FAF7F2] hover:text-[#2A1E17]" data-testid="about-events">
              <Calendar size={18} /> Browse Events
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
